import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { DashboardService } from 'src/app/dashboard.service';
import { LoadingService } from 'src/app/loading.service';
import { LocationService } from 'src/app/location.service';
import { MyBookingsService } from 'src/app/my-bookings.service';
import {
  IOrganisationObserver,
  OrganisationService,
} from 'src/app/organisation.service';
import { ConfirmDialogComponent } from 'src/app/utils/confirm-dialog/confirm-dialog.component';

interface BookingEntry {
  roomName: string;
  bookableName: string;
  userName: string;
  timeSlots: string;
  bookableId: string;
  userId: string;
  slots: number[];
}

@Component({
  selector: 'app-bookings-management',
  templateUrl: './bookings-management.component.html',
  styleUrls: ['./bookings-management.component.scss'],
})
export class BookingsManagementComponent
  implements OnInit, IOrganisationObserver
{
  observerId = 999;
  activeOrganisation: any;
  locations: any[] = [];
  selectedLocation: any = null;
  selectedDate: Date = new Date();
  bookings: BookingEntry[] = [];
  isLoading = false;
  error: string | null = null;

  constructor(
    private organisationService: OrganisationService,
    private locationService: LocationService,
    private dashboardService: DashboardService,
    private myBookingsService: MyBookingsService,
    private loadingService: LoadingService,
    private dialog: MatDialog,
    private translateService: TranslateService
  ) {}

  onOrganisationChange(organisations: any) {
    if (organisations && organisations.length > 0) {
      this.activeOrganisation = organisations[0];
      this.loadLocations();
    }
  }

  ngOnInit(): void {
    this.organisationService.subscribe(this);
  }

  ngOnDestroy(): void {
    this.organisationService.unsubscribe(this);
  }

  loadLocations() {
    this.locationService.getAllLocations().then((data: any) => {
      this.locations = data;
      if (this.locations.length > 0 && !this.selectedLocation) {
        this.selectedLocation = this.locations[0];
        this.loadBookings();
      }
    });
  }

  onLocationChange(locationId: string) {
    this.selectedLocation = this.locations.find((l) => l._id === locationId);
    this.loadBookings();
  }

  onDateChange(event: any) {
    if (event.target && event.target.value) {
      this.selectedDate = new Date(event.target.value);
      this.loadBookings();
    }
  }

  changeDay(direction: number) {
    const newDate = new Date(this.selectedDate);
    newDate.setDate(newDate.getDate() + direction);
    this.selectedDate = newDate;
    this.loadBookings();
  }

  loadBookings() {
    if (!this.selectedLocation || !this.activeOrganisation) {
      return;
    }

    this.isLoading = true;
    this.error = null;
    this.bookings = [];

    const dateString = this.formatDateForAPI(this.selectedDate);

    this.dashboardService
      .getAdminSlotsByLocationAndDate(
        this.selectedLocation._id,
        this.activeOrganisation._id,
        dateString
      )
      .then((data: any) => {
        this.processBookingsData(data);
        this.isLoading = false;
      })
      .catch((err) => {
        console.error('Error loading bookings:', err);
        this.error = this.translateService.instant(
          'Error loading bookings. Please try again.'
        );
        this.isLoading = false;
      });
  }

  processBookingsData(data: any) {
    const bookingsMap = new Map<string, BookingEntry>();

    // API returns a flat array of bookable items with blockedSlots
    if (Array.isArray(data)) {
      data.forEach((item: any) => {
        // Check if item has blocked slots/bookings
        if (item.blockedSlots && Array.isArray(item.blockedSlots) && item.blockedSlots.length > 0) {
          // Group blocked slots by user (using userIdHash since userId is not available)
          const userBookings = new Map<string, any>();

          item.blockedSlots.forEach((slot: any) => {
            const userId = slot.userIdHash || slot.userId || 'unknown';
            const userName = slot.userName || 'Unknown User';

            if (!userBookings.has(userId)) {
              userBookings.set(userId, {
                userId: userId,
                userName: userName,
                slots: [],
              });
            }
            userBookings.get(userId)!.slots.push(slot.slot);
          });

          // Create booking entries for each user
          userBookings.forEach((userBooking: any) => {
            const key = `${item.belongsToRoom}-${item.belongsToId}-${userBooking.userId}`;

            bookingsMap.set(key, {
              roomName: item.room || 'Unknown Room',
              bookableName: item.name || 'Unknown Bookable',
              userName: userBooking.userName,
              timeSlots: this.formatTimeSlots(userBooking.slots),
              bookableId: item.belongsToId,
              userId: userBooking.userId,
              slots: userBooking.slots,
            });
          });
        }
      });
    }

    // Convert map to array and sort
    this.bookings = Array.from(bookingsMap.values()).sort((a, b) => {
      // Sort by room, then bookable, then time
      if (a.roomName !== b.roomName) {
        return a.roomName.localeCompare(b.roomName);
      }
      if (a.bookableName !== b.bookableName) {
        return a.bookableName.localeCompare(b.bookableName);
      }
      return Math.min(...a.slots) - Math.min(...b.slots);
    });
  }

  formatTimeSlots(slots: number[]): string {
    if (!slots || slots.length === 0) return '';

    const sortedSlots = [...slots].sort((a, b) => a - b);
    const startSlot = sortedSlots[0];
    const endSlot = sortedSlots[sortedSlots.length - 1];

    return `${this.slotToTime(startSlot)} - ${this.slotToTime(endSlot + 1)}`;
  }

  slotToTime(slot: number): string {
    const hours = Math.floor(slot / 2);
    const minutes = slot % 2 === 0 ? '00' : '30';
    return `${hours.toString().padStart(2, '0')}:${minutes}`;
  }

  deleteBooking(booking: BookingEntry) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      maxWidth: '400px',
      data: {
        showCancel: true,
        cta: this.translateService.instant('Delete Booking'),
        dialogTitle: this.translateService.instant('Confirm Deletion'),
        dialogText: this.translateService.instant(
          'Are you sure you want to delete all bookings for {{user}} on {{bookable}}?',
          { user: booking.userName, bookable: booking.bookableName }
        ),
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.performDelete(booking);
      }
    });
  }

  performDelete(booking: BookingEntry) {
    this.isLoading = true;
    const dateString = this.formatDateForAPI(this.selectedDate);

    this.myBookingsService
      .adminDeleteBooking(
        booking.bookableId,
        booking.userName,
        dateString,
        this.activeOrganisation._id
      )
      .then(() => {
        this.isLoading = false;
        // Reload bookings to show updated list
        this.loadBookings();
      })
      .catch((err) => {
        console.error('Error deleting booking:', err);
        this.error = this.translateService.instant(
          'Error deleting booking. Please try again.'
        );
        this.isLoading = false;
      });
  }

  formatDateForAPI(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  formatDateForDisplay(date: Date): string {
    return date.toLocaleDateString(this.translateService.currentLang || 'en', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  getTodayForInput(): string {
    return this.formatDateForAPI(this.selectedDate);
  }
}
